import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Avatar,
  AvatarGroup,
  Rating,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { SectionVariable } from '../types';
import { useSettings } from '../Settings';
import { getAssetUrl, resolveAssetUrl } from '../api/assets';
import { AppMockup } from './AppMockup';
import { labelTagSx } from '../theme/tokens';
import { openSalesOrWhatsApp } from '../utils/links';
import { useTranslation, interpolate } from '../i18n';

interface AppSectionProps {
  variables?: SectionVariable;
  name?: string;
}

const CyanSmartphone = () => {
  const c = "#00D4FF";
  return (
    <svg width={28} height={28} viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3.83057 9.5766C3.83057 5.96526 3.83057 4.15911 4.95295 3.03769C6.07532 1.91627 7.88052 1.91531 11.4919 1.91531C15.1032 1.91531 16.9093 1.91531 18.0308 3.03769C19.1522 4.16007 19.1531 5.96526 19.1531 9.5766V13.4072C19.1531 17.0186 19.1531 18.8247 18.0308 19.9462C16.9084 21.0676 15.1032 21.0685 11.4919 21.0685C7.88052 21.0685 6.07437 21.0685 4.95295 19.9462C3.83152 18.8238 3.83057 17.0186 3.83057 13.4072V9.5766Z" stroke={c} strokeWidth="1.68"/>
      <path d="M14.3646 18.1956H8.61865" stroke={c} strokeWidth="1.68" strokeLinecap="round"/>
    </svg>
  );
};

const CyanHeart = () => {
  const c = "#00D4FF";
  return (
    <svg width={32} height={32} viewBox="0 0 41 41" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M9.6075 7.55764C6.77338 8.85256 4.69775 11.9344 4.69775 15.609C4.69775 19.3622 6.23525 22.2561 8.43559 24.7366C10.2515 26.7798 12.4485 28.4745 14.5907 30.1247C15.1009 30.5176 15.6049 30.9094 16.1026 31.3001C17.0012 32.009 17.8024 32.6291 18.5763 33.0818C19.3501 33.5346 19.9703 33.7396 20.4998 33.7396C21.0294 33.7396 21.6513 33.5346 22.4234 33.0818C23.1973 32.6291 23.9985 32.009 24.8971 31.3001C25.3948 30.9083 25.8987 30.5171 26.409 30.1264C28.5512 28.4728 30.7481 26.7798 32.5641 24.7366C34.7661 22.2561 36.3019 19.3622 36.3019 15.609C36.3019 11.9361 34.2263 8.85256 31.3922 7.55764C28.6383 6.2986 24.9381 6.63173 21.4223 10.2858C21.3028 10.4099 21.1595 10.5085 21.001 10.5759C20.8425 10.6432 20.6721 10.6779 20.4998 10.6779C20.3276 10.6779 20.1572 10.6432 19.9987 10.5759C19.8401 10.5085 19.6969 10.4099 19.5773 10.2858C16.0616 6.63173 12.3613 6.2986 9.6075 7.55764Z" fill="none" stroke={c} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
};

const CyanConn = () => {
  const c = "#00D4FF";
  return (
    <svg width={28} height={28} viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M11.4921 21.0685C16.7813 21.0685 21.0688 16.7811 21.0688 11.4919C21.0688 6.20276 16.7813 1.91531 11.4921 1.91531C6.20298 1.91531 1.91553 6.20276 1.91553 11.4919C1.91553 13.0242 2.27561 14.4722 2.91437 15.7564C3.08483 16.0973 3.14133 16.4871 3.04269 16.8558L2.47289 18.9875C2.41645 19.1985 2.4165 19.4206 2.47303 19.6315C2.52957 19.8425 2.64059 20.0348 2.79496 20.1893C2.94933 20.3438 3.14162 20.4549 3.35252 20.5116C3.56343 20.5682 3.78553 20.5684 3.99653 20.5121L6.12828 19.9414C6.4983 19.8479 6.88975 19.8932 7.22863 20.0687C8.55303 20.7282 10.0127 21.0704 11.4921 21.0685Z" stroke={c} strokeWidth="1.68"/>
      <path d="M11.4923 15.3226V7.66129M7.66162 13.4073V9.57661M15.3229 13.4073V9.57661" stroke={c} strokeWidth="1.68" strokeLinecap="round"/>
    </svg>
  );
};

const QueroChipButton = ({ onClick, label }: { onClick?: () => void; label: string }) => (
  <Button
    onClick={onClick}
    variant="contained"
    sx={{
      bgcolor: 'var(--color-primary, #6B00D6)',
      color: '#fff',
      borderRadius: '30px',
      textTransform: 'none',
      fontFamily: "'Figtree', sans-serif",
      fontWeight: 600,
      fontSize: '0.9rem',
      pl: 2.5,
      pr: 1,
      py: 1,
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      boxShadow: 'none',
      '&:hover': {
        bgcolor: 'var(--color-primary, #6B00D6)',
        boxShadow: 'none',
      }
    }}
  >
    {label}
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: '50%',
        bgcolor: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary, #6B00D6)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14M12 5l7 7-7 7" />
      </svg>
    </Box>
  </Button>
);

export function AppSection({ variables }: AppSectionProps) {
  const { app, settings } = useSettings();
  const { t } = useTranslation();
  const appRating = settings.appRating ?? 4.8;
  const appLinks = settings.appLinks;
  const images = variables?.image || [];

  const mobileImage = images[0] ? resolveAssetUrl(images[0], app) : '';
  const hideApp = settings.hideApp === true;

  function handleBuy() {
    const message = interpolate(t.whatsapp.salesMessage, {
      brand: settings.companyName || t.whatsapp.salesFallbackBrand,
    });
    openSalesOrWhatsApp(settings.salesUrl, settings.socials?.whatsapp, message);
  }

  return (
    <Box
      className="app-section"
      sx={{
        py: { xs: 6, md: 8 },
        px: { xs: 2, md: 4 },
        maxWidth: 1280,
        mx: 'auto',
      }}
    >
      <Grid container spacing={3}>
        {/* App Download Card */}
        {!hideApp && (
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper
            elevation={0}
            sx={{
              pt: { xs: 3, md: 4, lg: 5 },
              px: { xs: 3, md: 4, lg: 5 },
              pb: 0,
              borderRadius: '24px',
              bgcolor: 'var(--color-primary-dark, #4A0099)',
              color: '#fff',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap={{ xs: 'wrap', md: 'nowrap' }}>
              {/* Left Column within Card */}
              <Box>
                <Typography variant="h5" fontWeight={700} mb={3} fontFamily="'Figtree', sans-serif">
                  {t.appSection.titleLine1}
                  <br />
                  {t.appSection.titleLine2}
                </Typography>

                <Box display="flex" flexWrap="wrap" gap={1} mb={3} sx={{ maxWidth: '300px' }}>
                  {t.appSection.appBadges.map(
                    (item) => (
                      <Box
                        key={item}
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          borderRadius: '8px',
                          bgcolor: 'var(--color-primary, #6B00D6)',
                          fontSize: '0.7rem',
                          fontFamily: "'Figtree', sans-serif",
                          fontWeight: 500,
                        }}
                      >
                        {item}
                      </Box>
                    )
                  )}
                </Box>
              </Box>

              {/* Right Column within Card - Badges */}
              <Box display="flex" flexDirection="column" gap={1} alignItems={{ xs: 'flex-start', md: 'flex-end' }} minWidth="fit-content" mb={{ xs: 3, md: 0 }}>
                <Box
                  component={appLinks?.playStore ? 'a' : 'div'}
                  href={appLinks?.playStore}
                  target={appLinks?.playStore ? '_blank' : undefined}
                  rel={appLinks?.playStore ? 'noopener noreferrer' : undefined}
                  sx={{ display: 'flex' }}
                >
                  <Box
                    component="img"
                    src={getAssetUrl('images/app-section/google-play-badge.png', app)}
                    alt="Google Play"
                    sx={{ width: 128, height: 40, objectFit: 'contain', cursor: appLinks?.playStore ? 'pointer' : 'default' }}
                  />
                </Box>
                <Box
                  component={appLinks?.appStore ? 'a' : 'div'}
                  href={appLinks?.appStore}
                  target={appLinks?.appStore ? '_blank' : undefined}
                  rel={appLinks?.appStore ? 'noopener noreferrer' : undefined}
                  sx={{ display: 'flex' }}
                >
                  <Box
                    component="img"
                    src={getAssetUrl('images/app-section/app-store-badge.png', app)}
                    alt="App Store"
                    sx={{ width: 128, height: 40, objectFit: 'contain', cursor: appLinks?.appStore ? 'pointer' : 'default' }}
                  />
                </Box>
                <Typography sx={{ fontSize: '0.65rem', fontFamily: "'Figtree', sans-serif", fontWeight: 400, opacity: 0.8, maxWidth: 120, textAlign: { xs: 'left', md: 'right' }, mt: 0.5 }}>
                  {t.appSection.activateHint}
                </Typography>
              </Box>
            </Box>

            <AppMockup />
          </Paper>
        </Grid>
        )}

        {/* Social Proof Column */}
        <Grid size={{ xs: 12, md: hideApp ? 4 : 3.5 }}>
          <Box display="flex" flexDirection="column" gap={2} height="100%">
            {/* Social proof card */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: '16px',
                border: '1px solid #f0f0f0',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  ...labelTagSx,
                  width: 'fit-content',
                  mb: 2,
                }}
              >
                {t.appSection.socialProofTag}
              </Box>
              <Typography
                sx={{
                  color: '#4E4E4E',
                  fontFamily: "'Figtree', sans-serif",
                  fontSize: '1.75rem',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: '120%',
                  mb: 3
                }}
              >
                {interpolate(t.appSection.socialProofTitle, {
                  brand: settings.companyName || t.appSection.socialProofFallbackBrand,
                })}
              </Typography>
              <Box display="flex" justifyContent="flex-start" sx={{ mb: 2 }}>
                <AvatarGroup max={4} sx={{ '.MuiAvatar-root': { width: 32, height: 32, fontSize: '1rem' } }}>
                  <Avatar sx={{ bgcolor: 'var(--color-primary, #6B00D6)' }}>A</Avatar>
                  <Avatar sx={{ bgcolor: '#FF6B6B' }}>B</Avatar>
                  <Avatar sx={{ bgcolor: '#4ECDC4' }}>C</Avatar>
                </AvatarGroup>
              </Box>
              <Typography sx={{ color: '#888', mt: 'auto', fontFamily: "'Figtree', sans-serif", fontSize: '0.875rem' }}>
                {t.appSection.socialProofFooter}
              </Typography>
            </Paper>

            {!hideApp && (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: '1.5625rem',
                bgcolor: '#F5F5F5',
              }}
            >
              <Rating
                value={appRating}
                precision={0.1}
                readOnly
                size="small"
                icon={<StarIcon sx={{ color: '#FFB800' }} />}
                emptyIcon={<StarIcon sx={{ color: '#FFB8004D' }} />}
              />
              <Typography
                sx={{
                  color: '#000',
                  fontFamily: "'Figtree', sans-serif",
                  fontSize: '1.375rem',
                  fontWeight: 500,
                  lineHeight: '115.065%',
                  mt: 3,
                  mb: 1
                }}
              >
                {appRating} {t.appSection.ratingSuffix}
              </Typography>
              <Typography
                sx={{
                  color: '#5F5F5F',
                  fontFamily: "'Figtree', sans-serif",
                  fontSize: '0.8125rem',
                  lineHeight: 1.4,
                }}
              >
                {t.appSection.ratingFooter}
              </Typography>
            </Paper>
            )}
          </Box>
        </Grid>

        {/* Stacked Right Container */}
        <Grid size={{ xs: 12, md: hideApp ? 8 : 3.5 }}>
          <Box
            display="flex"
            flexDirection={hideApp ? { xs: 'column', md: 'row' } : 'column'}
            gap={3}
            height="100%"
          >
            {/* Top Purple Card */}
            <Paper
              elevation={0}
              sx={{
                flex: 1,
                minHeight: hideApp ? { xs: 220, md: 380 } : 220,
                borderRadius: '24px',
                bgcolor: 'var(--color-primary, #6B00D6)',
                p: { xs: 3, md: 4 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ fontFamily: "'Figtree', sans-serif", fontSize: { xs: '1.5rem', md: '1.75rem' }, fontWeight: 500, color: '#FFF', lineHeight: 1.2, mb: 4, pt: 1 }}>
                {t.appSection.purpleTitleLine1}
                <br />
                {t.appSection.purpleTitleLine2}
              </Typography>
              <Box display="flex" gap={2} alignItems="center" mt="auto">
                <CyanSmartphone />
                <CyanHeart />
                <CyanConn />
              </Box>
            </Paper>

            {/* Bottom Image Card */}
            <Paper
              elevation={0}
              sx={{
                flex: hideApp ? 1.5 : 1.5,
                minHeight: hideApp ? { xs: 300, md: 380 } : 300,
                borderRadius: '24px',
                bgcolor: '#f5f5f5',
                backgroundImage: `url(${mobileImage || getAssetUrl('images/woman-phone-2.png', app)})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                position: 'relative',
              }}
            >
              <Box sx={{ position: 'absolute', bottom: 24, left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
                <QueroChipButton onClick={handleBuy} label={t.common.iWantMyChip} />
              </Box>
            </Paper>

          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
